'use client';

import React, { useState, useRef, useEffect, useContext, useMemo } from "react";
import { Input } from '../ui/input';
import {
  Search,
  Bell,
  Settings,
  User,
  LogOut,
  ChevronDown,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  DollarSign,
  FileText,
  Receipt,
  Package,
  Fullscreen,
  ExternalLinkIcon,
} from 'lucide-react';
import { Button } from "../ui/button";
import { SidebarTrigger } from '../ui/sidebar';
import { AuthContext } from "../../context/AuthContext";
import { cn } from "../../utils/tailwindUtils";
import { Avatar } from "antd";
import male from "../../assets/svg/Male.svg";
import { useNavigate } from "react-router-dom";
import useApiMethods from "../../lib/useApiMethods";
import { API_URL, LOGOUT_URL, NOTIFICATION, NOTIFICATION_COUNT } from "../../constants/api";
import ToastMsg from "../common/ToastMsg";

export function NotificationDropdown() {
  const [notifications, setNotifications] = useState([]);
  const [notificationsCount, setNotificationsCount] = useState(null);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  const { fetch } = useApiMethods();

  const getNotifications = async () => {
    try {
      const response = await fetch(NOTIFICATION);
      if (response.status === 'success') {
        return response.data;
      }
      return [];
    } catch (error) {
      ToastMsg('error', error.message);
      return [];
    }
  };

  const getNotificationCount = async () => {
    try {
      const response = await fetch(NOTIFICATION_COUNT);
      if (response.status === 'success') {
        return response.data;
      }
      return 0;
    } catch (error) {
      ToastMsg('error', error.message);
      return 0;
    }
  };

  const fetchNotification = async () => {
    try {
      const [notificationData, unreadCount] = await Promise.all([
        getNotifications(),
        getNotificationCount()
      ]);

      console.log(notificationData);
      console.log(unreadCount);

      // Set the data to state
      setNotifications(notificationData || []);
      setNotificationsCount(unreadCount || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
      setNotificationsCount(0);
    }
  };

  useEffect(() => {
    fetchNotification();
  }, []); // Empty dependency array for initial fetch only

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get icon based on notification type and action
  const getNotificationIcon = (notification) => {
    const { notification_type, notification_action } = notification;

    // Leave notifications
    if (notification_type === 'Leave') {
      switch (notification_action) {
        case 'approved':
          return <CheckCircle className="h-4 w-4 text-green-600" />;
        case 'rejected':
          return <XCircle className="h-4 w-4 text-red-600" />;
        case 'request':
          return <FileText className="h-4 w-4 text-blue-600" />;
        default:
          return <Calendar className="h-4 w-4 text-blue-600" />;
      }
    }

    // Attendance notifications
    if (notification_type === 'Attendance') {
      return <Clock className="h-4 w-4 text-orange-600" />;
    }

    // Payroll notifications
    if (notification_type === 'Payroll') {
      return <DollarSign className="h-4 w-4 text-green-600" />;
    }

    // Reimbursement notifications
    if (notification_type === 'Reimbursement') {
      switch (notification_action) {
        case 'approved':
          return <CheckCircle className="h-4 w-4 text-green-600" />;
        case 'rejected':
          return <XCircle className="h-4 w-4 text-red-600" />;
        default:
          return <Receipt className="h-4 w-4 text-purple-600" />;
      }
    }

    // Asset notifications
    if (notification_type === 'Asset') {
      return <Package className="h-4 w-4 text-indigo-600" />;
    }

    // General/System notifications
    if (notification_type === 'System') {
      return <Settings className="h-4 w-4 text-gray-600" />;
    }

    // Default icon
    return <Bell className="h-4 w-4 text-gray-600" />;
  };

  // Format time function for relative time
  const formatRelativeTime = (timeString) => {
    if (!timeString) return '';
    const date = new Date(timeString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor(diffInHours * 60);
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const unreadCount = notifications.filter(n => !n.is_opened).length;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <Button
        onClick={() => setOpen((prev) => !prev)}
        variant="ghost"
        size="icon"
        className={cn(
          "relative rounded-full h-9 w-9",
          "text-muted-foreground hover:text-foreground",
          "bg-transparent hover:bg-accent",
          "transition-colors duration-200"
        )}
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
          </span>
        )}
      </Button>

      {/* Dropdown */}
      {open && (
        <div className={cn(
          "absolute right-0 mt-2 w-96 rounded-lg border bg-popover text-popover-foreground",
          "shadow-lg z-50 overflow-hidden",
          "animate-in fade-in-80 slide-in-from-top-2"
        )}>
          {/* Header */}
          <div className={cn(
            "flex items-center justify-between px-4 py-3",
            "border-b bg-muted/50"
          )}>
            <h3 className="text-sm font-semibold">Notifications</h3>
            {unreadCount > 0 && (
              <button className={cn(
                "text-xs font-medium text-primary",
                "hover:underline transition-colors"
              )}>
                Mark all as read
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="max-h-64 overflow-y-auto">
            {notifications.length > 0 ? (
              <ul className="divide-y divide-border">
                {notifications.map((notification) => (
                  <li
                    key={notification.id}
                    className={cn(
                      "px-4 py-3 cursor-pointer transition-colors",
                      "hover:bg-accent/50",
                      !notification.is_opened && "bg-accent/30"
                    )}
                    onClick={() => {
                      // Handle notification click
                      console.log('Notification clicked:', notification);
                    }}
                  >
                    <div className="flex gap-3">
                      {/* Notification Icon */}
                      <div className="flex-shrink-0 mt-0.5">
                        {getNotificationIcon(notification)}
                      </div>

                      {/* Notification Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <p className={cn(
                            "text-sm font-medium",
                            !notification.is_opened ? "text-foreground" : "text-muted-foreground"
                          )}>
                            {notification.title}
                          </p>
                          <div className={cn(
                            "flex-shrink-0 h-2 w-2 rounded-full ml-2 mt-1",
                            !notification.is_opened ? "bg-primary" : "bg-transparent"
                          )} />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {notification.message}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-muted-foreground">
                            {formatRelativeTime(notification.time)}
                          </span>
                          <span className={cn(
                            "text-xs px-2 py-1 rounded-full capitalize",
                            "bg-muted text-muted-foreground"
                          )}>
                            {notification.notification_type}
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-4 py-8 text-center">
                <Bell className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No notifications</p>
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className={cn(
              "px-4 py-3 text-center border-t bg-muted/50"
            )}>
              {/* <button className={cn(
                "text-xs font-medium text-primary",
                "hover:underline transition-colors"
              )}>
                View all notifications
              </button> */}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function UserDropdown() {
  const { logoutauth } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  const employee_name = sessionStorage.getItem('employee_name')
  const profile = sessionStorage.getItem('profile')
  const department = sessionStorage.getItem('department')
  const designation = sessionStorage.getItem('designation')
  const companyName = sessionStorage.getItem('companyName')

  const navigate = useNavigate();

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const useFullScreen = () => {
    const [isFullScreen, setIsFullScreen] = useState(false);

    const toggleFullScreen = async () => {
      try {
        if (!document.fullscreenElement) {
          await enterFullScreen();
        } else {
          await exitFullScreen();
        }
      } catch (error) {
        console.error('Full screen error:', error);
      }
    };

    const enterFullScreen = async () => {
      const docElement = document.documentElement;

      try {
        if (docElement.requestFullscreen) {
          await docElement.requestFullscreen();
        } else if (docElement.mozRequestFullScreen) {
          await docElement.mozRequestFullScreen();
        } else if (docElement.webkitRequestFullscreen) {
          await docElement.webkitRequestFullscreen();
        } else if (docElement.msRequestFullscreen) {
          await docElement.msRequestFullscreen();
        }
      } catch (error) {
        console.error('Error entering full screen:', error);
      }
    };

    const exitFullScreen = async () => {
      try {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.mozCancelFullScreen) {
          await document.mozCancelFullScreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        }
      } catch (error) {
        console.error('Error exiting full screen:', error);
      }
    };

    useEffect(() => {
      const handleFullScreenChange = () => {
        setIsFullScreen(!!document.fullscreenElement);
      };

      const events = [
        'fullscreenchange',
        'webkitfullscreenchange',
        'mozfullscreenchange',
        'MSFullscreenChange'
      ];

      events.forEach(event => {
        document.addEventListener(event, handleFullScreenChange);
      });

      return () => {
        events.forEach(event => {
          document.removeEventListener(event, handleFullScreenChange);
        });
      };
    }, []);

    return { isFullScreen, toggleFullScreen };
  };

  const { isFullScreen, toggleFullScreen } = useFullScreen();

  const menuItems = [
    // {
    //   icon: User,
    //   label: "My Profile",
    //   onClick: () => navigate("/employeeprofile"),
    //   description: "View and edit your profile"
    // },
    {
      icon: isFullScreen ? ExternalLinkIcon : Fullscreen, // Fixed: use ternary operator
      label: isFullScreen ? "Exit Full Screen" : "Full Screen",
      onClick: toggleFullScreen,
      description: isFullScreen ? "Exit full screen mode" : "Change full screen mode"
    },
    {
      icon: Settings,
      label: "Settings",
      onClick: () => navigate("/settings"),
      description: "Customize your preferences"
    },
  ];

  // your logout handler
  const handleLogout = async () => {
    try {
      const authtoken = sessionStorage.getItem("token");

      const requestOptions = {
        method: 'GET',
        headers: { Authorization: `token ${authtoken}` },
      };

      const res = await fetch(API_URL + LOGOUT_URL, requestOptions);
      const data = await res.json();

      if (data?.status === 'success') {
        logoutauth();
        navigate('/');
      } else {
        ToastMsg('error', data?.message || 'Logout failed');
      }
    } catch (error) {
      ToastMsg('error', error.message);
    }
  };

  return (
    <div className="relative font-sans" ref={menuRef}>
      {/* User Button */}
      <Button
        onClick={() => setOpen(!open)}
        variant="ghost"
        className={cn(
          "h-11 px-2 gap-2",
          open ? "bg-accent" :
            "bg-transparent hover:bg-accent",
          "text-foreground hover:text-foreground",
          "transition-colors duration-200",
          "border border-transparent hover:border-border"
        )}
      >
        <div className="h-7 w-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs font-semibold overflow-hidden">
          <Avatar
            src={profile || male}
            className="w-full h-full object-cover"
          />
        </div>
        {/* <div className="hidden sm:flex flex-col items-start">
          <span className="text-sm font-medium">{employee_name}</span>
          <span className="text-xs text-muted-foreground">{department}{" "}|{" "}{designation}</span>
        </div> */}
        <ChevronDown className={cn(
          "h-4 w-4 text-muted-foreground transition-transform",
          open && "rotate-180"
        )} />
      </Button>

      {/* Dropdown Menu */}
      {open && (
        <div
          className={cn(
            "absolute right-0 top-12 w-72 rounded-xl border shadow-lg z-50 overflow-hidden backdrop-blur-sm animate-in fade-in-80 slide-in-from-top-2 font-sans",

            // Light mode base
            "bg-white text-gray-800 border-gray-200",

            // Dark mode overrides
            "dark:bg-gray-900 dark:text-gray-100 dark:border-gray-700"
          )}
        >
          {/* User Info Header */}
          <div className="px-5 py-4 border-b 
      bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/30 dark:to-purple-950/30 
      border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center overflow-hidden">
                <Avatar
                  src={profile || male}
                  size="large"
                  className="cursor-pointer scale-125 hover:shadow-lg"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{employee_name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{department}{" "}|{" "}{designation}</p>
              </div>
            </div>
            <p className="text-xs text-blue-600 dark:text-blue-400 font-medium truncate">
              {companyName}
            </p>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {menuItems.map((item, index) => (
              <button
                key={index}
                onClick={item.onClick}
                className={cn(
                  "w-full flex items-start gap-3 px-5 py-3 text-sm group transition-all duration-150 ease-in-out border-l-2 border-l-transparent font-sans",

                  // Light mode hover
                  "hover:bg-gray-100 hover:text-gray-900 hover:border-l-sidebar",

                  // Dark mode hover
                  "dark:hover:bg-gray-800 dark:hover:text-gray-100 dark:hover:border-l-blue-400"
                )}
              >
                <div className="flex-shrink-0 h-5 w-5 flex items-center justify-center rounded-md bg-gray-100 text-gray-600 
            group-hover:bg-blue-50 group-hover:text-sidebar
            dark:bg-gray-800 dark:text-gray-400 dark:group-hover:bg-blue-950/30 dark:group-hover:text-blue-400"
                >
                  <item.icon className="h-3.5 w-3.5" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium">{item.label}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300">
                    {item.description}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Logout Section */}
          <div className="border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleLogout}
              className={cn(
                "w-full flex items-center gap-3 px-5 py-3 text-sm group transition-all duration-150 ease-in-out border-l-2 border-l-transparent",

                // Light mode
                "text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-l-red-500",

                // Dark mode
                "dark:text-red-400 dark:hover:bg-red-900/40 dark:hover:text-red-300 dark:hover:border-l-red-500"
              )}
            >
              <div className="flex-shrink-0 h-5 w-5 flex items-center justify-center rounded-md 
          bg-red-100 text-red-600 group-hover:text-red-700
          dark:bg-red-900/30 dark:text-red-400 dark:group-hover:text-red-300"
              >
                <LogOut className="h-3.5 w-3.5" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium">Log out</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Sign out of your account</p>
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function SiteHeader({ menuItems }) {
  const mainNav = menuItems.filter(item => !item.isSecondary);
  const secondaryNav = menuItems.filter(item => item.isSecondary);

  const [searchQuery, setSearchQuery] = useState('');
  const [filteredItems, setFilteredItems] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const navigate = useNavigate(); // or useRouter() if using Next.js

  // Flatten all menu items including subItems for search
  const allMenuItems = useMemo(() => {
    const items = [];

    const flattenItems = (itemList, parentLabel = '') => {
      itemList.forEach(item => {
        // Add current item
        items.push({
          ...item,
          parentLabel: parentLabel,
          fullPath: item.href,
          displayLabel: parentLabel ? `${parentLabel} > ${item.label}` : item.label
        });

        // Recursively add subItems
        if (item.subItems && item.subItems.length > 0) {
          flattenItems(item.subItems, item.label);
        }
      });
    };

    flattenItems([...mainNav, ...secondaryNav]);
    return items;
  }, [mainNav, secondaryNav]);

  // Filter items based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredItems([]);
      setIsSearchOpen(false);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = allMenuItems.filter(item =>
      item.label.toLowerCase().includes(query) ||
      item.parentLabel.toLowerCase().includes(query) ||
      item.href.toLowerCase().includes(query)
    ).slice(0, 8); // Limit to 8 results

    setFilteredItems(filtered);
    setIsSearchOpen(true);
  }, [searchQuery]);

  // Handle item selection
  const handleItemSelect = (item) => {
    navigate(item.href);
    setSearchQuery('');
    setIsSearchOpen(false);
  };

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.search-container')) {
        setIsSearchOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <header className={cn(
      "sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
      "transition-colors duration-200"
    )}>
      <div className="flex h-14 items-center justify-between px-4 sm:px-6">
        {/* Mobile Sidebar Trigger */}
        <div className="flex items-center md:hidden">
          <SidebarTrigger />
        </div>

        {/* Search Bar with Quick Navigation */}
        <div className="relative hidden md:block flex-1 max-w-sm lg:max-w-md search-container">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Quick navigate..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => searchQuery && setIsSearchOpen(true)}
              className={cn(
                "pl-9 pr-4 h-9 w-full rounded-lg",
                "bg-muted/50 border-border",
                "placeholder:text-muted-foreground",
                "transition-colors duration-200",
                "shadow-sm"
              )}
            />

            {/* Search Results Dropdown */}
            {isSearchOpen && filteredItems.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                <div className="p-2">
                  <div className="text-xs font-medium text-muted-foreground px-2 py-1">
                    Quick Navigation
                  </div>
                  {filteredItems.map((item, index) => (
                    <button
                      key={`${item.href}-${index}`}
                      onClick={() => handleItemSelect(item)}
                      className={cn(
                        "w-full text-left px-3 py-2 rounded-md",
                        "hover:bg-accent hover:text-accent-foreground",
                        "transition-colors duration-150",
                        "flex items-center gap-2"
                      )}
                    >
                      {/* Item Icon */}
                      {item.icon && (
                        <div className="flex-shrink-0 w-4 h-4 text-muted-foreground">
                          {React.createElement(item.icon, { className: "w-4 h-4" })}
                        </div>
                      )}

                      {/* Item Details */}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {item.label}
                        </div>
                        {item.parentLabel && (
                          <div className="text-xs text-muted-foreground truncate">
                            {item.parentLabel}
                          </div>
                        )}
                      </div>

                      {/* Shortcut Hint */}
                      <div className="flex-shrink-0 text-xs text-muted-foreground">
                        ↵
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* No Results State */}
            {isSearchOpen && searchQuery && filteredItems.length === 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg z-50">
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No results found for "{searchQuery}"
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-2">
          <NotificationDropdown />
          <UserDropdown />
        </div>
      </div>
    </header>
  );
}